import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../Components/Common/HelpArticleLink";
import { HELP_ARTICLE_SETTINGS_OPERATION } from "../../../helpers/helpCenter";
import { formatMoneyFromCents } from "../../../helpers/money";
import {
  DEFAULT_TENANT_SETTINGS,
  getSettings,
  TenantSettings,
  updateSettings,
} from "../../../services/tenantSettingService";

const SAMPLE_ORDER_SUBTOTAL_CENTS = 20000;
/** Sample retail sale for commission-on-products preview (GAP-F2-138 polish). */
const SAMPLE_PRODUCT_SALE_CENTS = 5000;
const SAMPLE_PRODUCT_COMMISSION_BPS = 2000;

const OperationSettings = () => {
  const { t, i18n } = useTranslation();

  const [settings, setSettings] = useState<TenantSettings>(DEFAULT_TENANT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [updatingCommissionToggle, setUpdatingCommissionToggle] = useState(false);
  const [updatingCommissionOnProducts, setUpdatingCommissionOnProducts] =
    useState(false);
  const [updatingCommissionCalcFlag, setUpdatingCommissionCalcFlag] = useState<
    string | null
  >(null);
  const [updatingCashMode, setUpdatingCashMode] = useState(false);
  const [updatingReminderHour, setUpdatingReminderHour] = useState(false);
  const [updatingNearGoalHour, setUpdatingNearGoalHour] = useState(false);
  const [updatingDiscountCap, setUpdatingDiscountCap] = useState(false);
  const [updatingReceptionistDiscount, setUpdatingReceptionistDiscount] = useState(false);
  const [updatingDigestEmail, setUpdatingDigestEmail] = useState(false);
  const [updatingDigestManagers, setUpdatingDigestManagers] = useState(false);
  const [updatingHighlight, setUpdatingHighlight] = useState(false);

  useEffect(() => {
    document.title = t("settings.operation.pageTitle");
  }, [t]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSettings();
      setSettings(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleCommissionTrackingToggle = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.checked;
    setUpdatingCommissionToggle(true);

    try {
      const response = await updateSettings({
        use_commission_tracking: nextValue,
      });
      setSettings(response.data);
      toast.success(response.message);
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingCommissionToggle(false);
    }
  };

  const handleCommissionOnProductsToggle = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.checked;
    setUpdatingCommissionOnProducts(true);

    try {
      const response = await updateSettings({
        commission_on_products: nextValue,
      });
      setSettings(response.data);
      toast.success(
        response.message ||
          t("settings.general.operation.commissionOnProducts.toastSaved")
      );
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingCommissionOnProducts(false);
    }
  };

  const handleCommissionCalculationToggle = async (
    flag:
      | "deduct_proportional_discount"
      | "deduct_gateway_fee_share"
      | "deduct_product_cogs",
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.checked;
    setUpdatingCommissionCalcFlag(flag);

    try {
      const response = await updateSettings({
        commission_calculation: {
          [flag]: nextValue,
        },
      });
      setSettings(response.data);
      toast.success(
        response.message || t("settings.general.operation.commissionCalc.toastSaved")
      );
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingCommissionCalcFlag(null);
    }
  };

  const handleCashRegisterModeChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.value as "unified" | "per_professional";
    setUpdatingCashMode(true);

    try {
      const response = await updateSettings({
        cash_register_mode: nextValue,
      });
      setSettings(response.data);
      toast.success(response.message);
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingCashMode(false);
    }
  };

  const handleReminderHourChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const raw = event.target.value;
    const nextValue = raw === "" ? null : Number(raw);
    setUpdatingReminderHour(true);

    try {
      const response = await updateSettings({
        day_close_reminder_hour: nextValue,
      });
      setSettings(response.data);
      toast.success(response.message);
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingReminderHour(false);
    }
  };

  const handleNearGoalHourChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const raw = event.target.value;
    const nextValue = raw === "" ? null : Number(raw);
    setUpdatingNearGoalHour(true);

    try {
      const response = await updateSettings({
        near_goal_reminder_hour: nextValue,
      });
      setSettings(response.data);
      toast.success(response.message);
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingNearGoalHour(false);
    }
  };

  const handleDiscountCapChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const percent = Number(event.target.value);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      return;
    }

    setUpdatingDiscountCap(true);

    try {
      const response = await updateSettings({
        max_manual_discount_bps: Math.round(percent * 100),
      });
      setSettings(response.data);
      toast.success(
        response.message || t("settings.general.operation.discount.toastSaved")
      );
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingDiscountCap(false);
    }
  };

  const handleReceptionistDiscountToggle = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.checked;
    setUpdatingReceptionistDiscount(true);

    try {
      const response = await updateSettings({
        allow_receptionist_discount: nextValue,
      });
      setSettings(response.data);
      toast.success(
        response.message || t("settings.general.operation.discount.toastSaved")
      );
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingReceptionistDiscount(false);
    }
  };

  const handleDigestEmailToggle = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.checked;
    setUpdatingDigestEmail(true);

    try {
      const response = await updateSettings({
        courtesy_digest_email_enabled: nextValue,
      });
      setSettings(response.data);
      toast.success(
        response.message || t("settings.general.operation.discount.toastSaved")
      );
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingDigestEmail(false);
    }
  };

  const handleDigestManagersToggle = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.checked;
    setUpdatingDigestManagers(true);

    try {
      const response = await updateSettings({
        courtesy_digest_include_managers: nextValue,
      });
      setSettings(response.data);
      toast.success(
        response.message || t("settings.general.operation.discount.toastSaved")
      );
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingDigestManagers(false);
    }
  };

  const handleHighlightChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const percent = Number(event.target.value);
    if (!Number.isFinite(percent) || percent < 1 || percent > 100) {
      return;
    }

    setUpdatingHighlight(true);

    try {
      const response = await updateSettings({
        courtesy_highlight_bps: Math.round(percent * 100),
      });
      setSettings(response.data);
      toast.success(
        response.message || t("settings.general.operation.discount.toastSaved")
      );
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingHighlight(false);
    }
  };

  const reminderSelectValue =
    settings.day_close_reminder_hour === null ||
    settings.day_close_reminder_hour === undefined
      ? ""
      : String(settings.day_close_reminder_hour);

  const nearGoalSelectValue =
    settings.near_goal_reminder_hour === null ||
    settings.near_goal_reminder_hour === undefined
      ? ""
      : String(settings.near_goal_reminder_hour);

  return (
    <div className="page-content">
      <ToastContainer closeButton pauseOnHover />
      <Container fluid>
        <BreadCrumb
          title={t("settings.operation.breadcrumb.title")}
          pageTitle={t("settings.operation.breadcrumb.pageTitle")}
        />
        <div className="d-flex justify-content-end mb-3">
          <HelpArticleLink articlePath={HELP_ARTICLE_SETTINGS_OPERATION} />
        </div>
        <Row className="justify-content-center">
          <Col lg={8} xl={7}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  {t("settings.general.operation.title")}
                </h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex justify-content-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : (
                  <>
                    <div className="form-check form-switch mb-3">
                      <Input
                        id="use-commission-tracking"
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        checked={settings.use_commission_tracking}
                        disabled={updatingCommissionToggle}
                        onChange={handleCommissionTrackingToggle}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="use-commission-tracking"
                      >
                        {t("settings.general.operation.commissionToggleLabel")}
                      </Label>
                    </div>
                    <p className="text-muted small mb-4">
                      {t("settings.general.operation.commissionToggleHelp")}
                    </p>

                    {settings.use_commission_tracking ? (
                      <div className="form-check form-switch mb-2">
                        <Input
                          id="commission-on-products"
                          type="checkbox"
                          role="switch"
                          className="form-check-input"
                          checked={Boolean(settings.commission_on_products)}
                          disabled={updatingCommissionOnProducts}
                          onChange={handleCommissionOnProductsToggle}
                        />
                        <Label
                          className="form-check-label"
                          htmlFor="commission-on-products"
                        >
                          {t(
                            "settings.general.operation.commissionOnProducts.label"
                          )}
                        </Label>
                      </div>
                    ) : null}
                    {settings.use_commission_tracking ? (
                      <>
                        <p className="text-muted small mb-1">
                          {t(
                            "settings.general.operation.commissionOnProducts.help"
                          )}
                        </p>
                        <p className="text-muted small mb-4">
                          {t(
                            "settings.general.operation.commissionOnProducts.example",
                            {
                              product: formatMoneyFromCents(
                                SAMPLE_PRODUCT_SALE_CENTS,
                                i18n.language
                              ),
                              percent: (
                                SAMPLE_PRODUCT_COMMISSION_BPS / 100
                              ).toFixed(0),
                              commission: formatMoneyFromCents(
                                Math.floor(
                                  (SAMPLE_PRODUCT_SALE_CENTS *
                                    SAMPLE_PRODUCT_COMMISSION_BPS) /
                                    10000
                                ),
                                i18n.language
                              ),
                            }
                          )}
                        </p>
                      </>
                    ) : null}

                    {settings.use_commission_tracking ? (
                      <div className="mb-4 ps-1 border-start border-2 border-light">
                        <h6 className="mb-2">
                          {t("settings.general.operation.commissionCalc.sectionTitle")}
                        </h6>
                        <p className="text-muted small mb-3">
                          {t("settings.general.operation.commissionCalc.sectionHelp")}
                        </p>

                        {(
                          [
                            "deduct_proportional_discount",
                            "deduct_gateway_fee_share",
                            "deduct_product_cogs",
                          ] as const
                        ).map((flag) => (
                          <div key={flag} className="form-check form-switch mb-2">
                            <Input
                              id={`commission-calc-${flag}`}
                              type="checkbox"
                              role="switch"
                              className="form-check-input"
                              checked={Boolean(
                                settings.commission_calculation?.[flag] ??
                                  (flag === "deduct_proportional_discount")
                              )}
                              disabled={
                                updatingCommissionCalcFlag !== null ||
                                !settings.use_commission_tracking
                              }
                              onChange={(event) =>
                                handleCommissionCalculationToggle(flag, event)
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor={`commission-calc-${flag}`}
                            >
                              {t(
                                `settings.general.operation.commissionCalc.${flag}.label`
                              )}
                            </Label>
                            <p className="text-muted small mb-0 ms-4">
                              {t(
                                `settings.general.operation.commissionCalc.${flag}.help`
                              )}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <hr className="my-4" />

                    <h6 className="mb-3">
                      {t("settings.general.operation.discount.sectionTitle")}
                    </h6>

                    <div className="mb-2">
                      <Label htmlFor="max-manual-discount-percent">
                        {t("settings.general.operation.discount.capLabel")}
                      </Label>
                      <Input
                        id="max-manual-discount-percent"
                        type="select"
                        value={String(
                          Math.round((settings.max_manual_discount_bps ?? 2000) / 100)
                        )}
                        disabled={updatingDiscountCap}
                        onChange={handleDiscountCapChange}
                      >
                        {Array.from(
                          new Set([
                            5,
                            10,
                            15,
                            20,
                            25,
                            30,
                            40,
                            50,
                            Math.round(
                              (settings.max_manual_discount_bps ?? 2000) / 100
                            ),
                          ])
                        )
                          .filter((percent) => percent >= 0 && percent <= 100)
                          .sort((a, b) => a - b)
                          .map((percent) => (
                          <option key={percent} value={percent}>
                            {t("settings.general.operation.discount.capOption", {
                              percent,
                            })}
                          </option>
                        ))}
                      </Input>
                    </div>
                    <p className="text-muted small mb-2">
                      {t("settings.general.operation.discount.capHelp")}
                    </p>
                    <p className="text-muted small mb-4">
                      {t("settings.general.operation.discount.capExample", {
                        percent: Math.round(
                          (settings.max_manual_discount_bps ?? 2000) / 100
                        ),
                        sample: formatMoneyFromCents(
                          SAMPLE_ORDER_SUBTOTAL_CENTS,
                          i18n.language
                        ),
                        amount: formatMoneyFromCents(
                          Math.floor(
                            (SAMPLE_ORDER_SUBTOTAL_CENTS *
                              (settings.max_manual_discount_bps ?? 2000)) /
                              10000
                          ),
                          i18n.language
                        ),
                      })}
                    </p>

                    <div className="form-check form-switch mb-2">
                      <Input
                        id="allow-receptionist-discount"
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        checked={Boolean(settings.allow_receptionist_discount)}
                        disabled={updatingReceptionistDiscount}
                        onChange={handleReceptionistDiscountToggle}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="allow-receptionist-discount"
                      >
                        {t(
                          "settings.general.operation.discount.receptionistLabel"
                        )}
                      </Label>
                    </div>
                    <p className="text-muted small mb-4">
                      {t("settings.general.operation.discount.receptionistHelp")}
                    </p>

                    <div className="form-check form-switch mb-2">
                      <Input
                        id="courtesy-digest-email"
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        checked={Boolean(
                          settings.courtesy_digest_email_enabled ?? true
                        )}
                        disabled={updatingDigestEmail}
                        onChange={handleDigestEmailToggle}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="courtesy-digest-email"
                      >
                        {t("settings.general.operation.discount.digestEmailLabel")}
                      </Label>
                    </div>
                    <p className="text-muted small mb-3">
                      {t("settings.general.operation.discount.digestEmailHelp")}
                    </p>

                    <div className="form-check form-switch mb-2">
                      <Input
                        id="courtesy-digest-managers"
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        checked={Boolean(
                          settings.courtesy_digest_include_managers
                        )}
                        disabled={
                          updatingDigestManagers ||
                          !(settings.courtesy_digest_email_enabled ?? true)
                        }
                        onChange={handleDigestManagersToggle}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="courtesy-digest-managers"
                      >
                        {t(
                          "settings.general.operation.discount.digestManagersLabel"
                        )}
                      </Label>
                    </div>
                    <p className="text-muted small mb-4">
                      {t(
                        "settings.general.operation.discount.digestManagersHelp"
                      )}
                    </p>

                    <div className="mb-2">
                      <Label htmlFor="courtesy-highlight-percent">
                        {t(
                          "settings.general.operation.discount.highlightLabel"
                        )}
                      </Label>
                      <Input
                        id="courtesy-highlight-percent"
                        type="select"
                        value={String(
                          Math.round(
                            (settings.courtesy_highlight_bps ?? 1500) / 100
                          )
                        )}
                        disabled={updatingHighlight}
                        onChange={handleHighlightChange}
                      >
                        {[5, 10, 15, 20, 25, 30].map((percent) => (
                          <option key={percent} value={percent}>
                            {t(
                              "settings.general.operation.discount.highlightOption",
                              { percent }
                            )}
                          </option>
                        ))}
                      </Input>
                    </div>
                    <p className="text-muted small mb-4">
                      {t("settings.general.operation.discount.highlightHelp")}
                    </p>

                    <hr className="my-4" />

                    <div className="mb-2">
                      <Label htmlFor="cash-register-mode">
                        {t("settings.general.operation.cashRegisterModeLabel")}
                      </Label>
                      <Input
                        id="cash-register-mode"
                        type="select"
                        value={settings.cash_register_mode || "unified"}
                        disabled={updatingCashMode}
                        onChange={handleCashRegisterModeChange}
                      >
                        <option value="unified">
                          {t(
                            "settings.general.operation.cashRegisterMode.unified"
                          )}
                        </option>
                        <option value="per_professional">
                          {t(
                            "settings.general.operation.cashRegisterMode.per_professional"
                          )}
                        </option>
                      </Input>
                    </div>
                    <p className="text-muted small mb-4">
                      {t("settings.general.operation.cashRegisterModeHelp")}
                    </p>

                    <div className="mb-2">
                      <Label htmlFor="day-close-reminder-hour">
                        {t(
                          "settings.general.operation.dayCloseReminderHourLabel"
                        )}
                      </Label>
                      <Input
                        id="day-close-reminder-hour"
                        type="select"
                        value={reminderSelectValue}
                        disabled={updatingReminderHour}
                        onChange={handleReminderHourChange}
                      >
                        <option value="">
                          {t(
                            "settings.general.operation.dayCloseReminderHour.auto",
                            {
                              hour:
                                settings.day_close_reminder_hour_resolved ?? 18,
                            }
                          )}
                        </option>
                        {Array.from({ length: 24 }, (_, hour) => (
                          <option key={hour} value={hour}>
                            {t(
                              "settings.general.operation.dayCloseReminderHour.fixed",
                              {
                                hour: String(hour).padStart(2, "0"),
                              }
                            )}
                          </option>
                        ))}
                      </Input>
                    </div>
                    <p className="text-muted small mb-4">
                      {t("settings.general.operation.dayCloseReminderHourHelp")}
                    </p>

                    <div className="mb-2">
                      <Label htmlFor="near-goal-reminder-hour">
                        {t(
                          "settings.general.operation.nearGoalReminderHourLabel"
                        )}
                      </Label>
                      <Input
                        id="near-goal-reminder-hour"
                        type="select"
                        value={nearGoalSelectValue}
                        disabled={updatingNearGoalHour}
                        onChange={handleNearGoalHourChange}
                      >
                        <option value="">
                          {t(
                            "settings.general.operation.nearGoalReminderHour.auto",
                            {
                              hour:
                                settings.near_goal_reminder_hour_resolved ?? 16,
                            }
                          )}
                        </option>
                        {Array.from({ length: 24 }, (_, hour) => (
                          <option key={hour} value={hour}>
                            {t(
                              "settings.general.operation.nearGoalReminderHour.fixed",
                              {
                                hour: String(hour).padStart(2, "0"),
                              }
                            )}
                          </option>
                        ))}
                      </Input>
                    </div>
                    <p className="text-muted small mb-0">
                      {t("settings.general.operation.nearGoalReminderHourHelp")}
                    </p>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default OperationSettings;
